import { useEffect, useState } from "react";
import { api, errText } from "../lib/api";
import { Avatar, Loading } from "./common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Trophy, Plus, Wand2, Users as UsersIcon, Vote, Lock, Eye, Archive, Trash2, Power, Crown, Heart } from "lucide-react";

const METRICS = ["overall", "followers", "engagement", "streams", "views", "reels"];
const PHASES = [
  ["qualification", "Qualification"],
  ["top10_voting", "Top 10 Voting"],
  ["finalist_voting", "Finalist Voting"],
  ["reveal", "Reveal"],
];

export function AwardsAdmin() {
  const [d, setD] = useState(null);
  const [fanNoms, setFanNoms] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", platform: "global", metric: "overall", type: "standard" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/awards/dashboard").then(({ data }) => setD(data)).catch((e) => toast.error(errText(e)));
  useEffect(() => { load(); api.get("/admin/awards/fan-nominations").then(({ data }) => setFanNoms(data)).catch(() => {}); }, []);

  const act = async (fn, msg) => {
    setBusy(true);
    try { const r = await fn(); if (msg) toast.success(typeof msg === "function" ? msg(r) : msg); load(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  if (!d) return <Loading />;
  const yr = d.year;
  const platforms = d.platforms || [];

  return (
    <div className="space-y-5">
      {/* Cycle header */}
      <div className="surface rounded-2xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 text-gold text-xs font-semibold tracking-widest uppercase"><Trophy size={14} /> VirtuAwards Cycle</div>
            <div className="font-display font-bold text-2xl mt-1">{yr.year} · <span className="capitalize text-gold">{yr.phase.replace("_", " ")}</span></div>
            <div className="text-xs text-muted-foreground mt-1">Show date {new Date(yr.show_date + "T00:00:00").toLocaleDateString()} · {d.eligible_users} eligible users · {d.fan_nominations} fan nominations</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Btn onClick={() => act(() => api.post("/admin/awards/generate-top10-all"), (r) => `Generated ${r.data.nominees} nominees`)} disabled={busy} icon={Wand2} testid="awards-gen-top10">Generate Top 10 (All)</Btn>
            <Btn onClick={() => act(() => api.post("/admin/awards/generate-top5-all"), "Top 5 finalists generated")} disabled={busy} icon={UsersIcon} testid="awards-gen-top5">Generate Top 5 (All)</Btn>
            <Btn onClick={() => act(() => api.post("/admin/awards/lock-all"), (r) => `Locked ${r.data.locked} winners`)} disabled={busy} icon={Lock} testid="awards-lock-all">Lock All Winners</Btn>
          </div>
        </div>

        {/* Phase controls */}
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">Set phase:</span>
          {PHASES.map(([p, label]) => (
            <button key={p} onClick={() => act(() => api.post("/admin/awards/phase", { phase: p }), `Phase → ${label}`)} disabled={busy}
              data-testid={`awards-phase-${p}`} className={`px-3 py-1.5 rounded-full text-xs font-medium ${yr.phase === p ? "bg-gold text-black" : "surface-2 text-muted-foreground hover:text-white"}`}>{label}</button>
          ))}
          <Btn onClick={() => { if (window.confirm("Archive this year and open a new cycle? Historical data is preserved.")) act(() => api.post("/admin/awards/archive-year"), (r) => `Archived ${r.data.archived}, opened ${r.data.new_year}`); }} disabled={busy} icon={Archive} testid="awards-archive" tone="danger">Archive Year</Btn>
        </div>

        {/* Fan settings */}
        <div className="mt-3 flex items-center gap-3 flex-wrap text-xs">
          <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={!!yr.fan_nominations_open} onChange={(e) => act(() => api.post("/admin/awards/fan-settings", { fan_nominations_open: e.target.checked }), "Updated")} data-testid="fan-noms-toggle" /> Fan nominations open</label>
          <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={!!yr.fan_self_nominate} onChange={(e) => act(() => api.post("/admin/awards/fan-settings", { fan_self_nominate: e.target.checked }), "Updated")} /> Allow self-nomination</label>
        </div>
      </div>

      {/* Create category */}
      <div className="surface rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3 font-display font-semibold"><Plus size={16} className="text-gold" /> Create Category</div>
        <div className="grid sm:grid-cols-2 gap-2">
          <Input placeholder="Category name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="surface-2 border-white/10" data-testid="new-cat-name" />
          <Input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="surface-2 border-white/10" />
          <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className="surface-2 border border-white/10 rounded-xl px-3 py-2.5 text-sm" data-testid="new-cat-platform">
            {platforms.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
          <select value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value })} className="surface-2 border border-white/10 rounded-xl px-3 py-2.5 text-sm capitalize">
            {METRICS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="surface-2 border border-white/10 rounded-xl px-3 py-2.5 text-sm">
            <option value="standard">Standard (auto Top 10)</option>
            <option value="fan_favorite">Fan Favorite (nominated)</option>
          </select>
          <Button className="btn-gold rounded-xl" disabled={busy || !form.name} data-testid="new-cat-submit"
            onClick={() => act(async () => { await api.post("/admin/awards/categories", form); setForm({ name: "", description: "", platform: "global", metric: "overall", type: "standard" }); }, "Category created")}>Add Category</Button>
        </div>
      </div>

      {/* Fan nominations review */}
      {fanNoms && fanNoms.length > 0 && (
        <div className="surface rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3 font-display font-semibold"><Heart size={16} className="text-rose-400" /> Fan Favorite Nominations</div>
          <div className="space-y-2">
            {fanNoms.map((f) => (
              <div key={f.user.id} className="flex items-center gap-3 surface-2 rounded-xl p-2.5">
                <Avatar src={f.user.avatar_url} name={f.user.display_name} size={34} />
                <div className="flex-1 min-w-0"><div className="text-sm truncate">{f.user.display_name}</div><div className="text-xs text-muted-foreground">@{f.user.username} · {f.nominations} nominations</div></div>
                <button onClick={() => act(() => api.delete(`/admin/awards/fan-nominations/${f.user.id}`), "Removed")} className="text-rose-400 hover:text-rose-300 p-1.5" title="Remove (fraud)"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Categories */}
      <div className="space-y-2">
        <div className="font-display font-semibold">Categories ({d.categories.length})</div>
        {d.categories.map((c) => (
          <div key={c.id} className={`surface rounded-xl p-4 ${!c.active ? "opacity-50" : ""}`} data-testid={`admin-award-cat-${c.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-gold">{c.platform === "global" ? "Global" : c.platform} · {c.metric}{c.type === "fan_favorite" ? " · Fan Favorite" : ""}</div>
                <div className="font-medium">{c.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{c.nominee_count} nominees · {c.finalist_count} finalists · {c.vote_count} votes{c.winner ? ` · winner: ${c.winner.user?.display_name}${c.winner.revealed ? " (revealed)" : c.winner.locked ? " (locked)" : ""}` : ""}</div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <IconBtn onClick={() => act(() => api.post(`/admin/awards/categories/${c.id}/generate-top10`), (r) => `${r.data.count} nominees`)} icon={Wand2} title="Generate Top 10" testid={`cat-top10-${c.id}`} />
                <IconBtn onClick={() => act(() => api.post(`/admin/awards/categories/${c.id}/generate-top5`), (r) => `${r.data.count} finalists`)} icon={UsersIcon} title="Generate Top 5" />
                <IconBtn onClick={() => act(() => api.post(`/admin/awards/categories/${c.id}/lock-winner`), (r) => `Winner locked: ${r.data.winner?.display_name}`)} icon={Lock} title="Lock Winner" />
                <IconBtn onClick={() => act(() => api.post(`/admin/awards/categories/${c.id}/reveal`), "Winner revealed")} icon={Eye} title="Reveal Winner" testid={`cat-reveal-${c.id}`} tone="gold" />
                <IconBtn onClick={() => act(() => api.post(`/admin/awards/categories/${c.id}/toggle`), "Toggled")} icon={Power} title="Enable/Disable" />
                <IconBtn onClick={() => { if (window.confirm("Delete this category?")) act(() => api.delete(`/admin/awards/categories/${c.id}`), "Deleted"); }} icon={Trash2} title="Delete" tone="danger" />
              </div>
            </div>
            {c.winner?.revealed && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-gold"><Crown size={13} /> {c.winner.user?.display_name} won this category</div>
            )}
          </div>
        ))}
      </div>

      {/* Archived years */}
      {d.archived_years?.length > 0 && (
        <div className="surface rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2 font-display font-semibold"><Archive size={16} /> Archived Years</div>
          <div className="flex flex-wrap gap-2">{d.archived_years.map((y) => <span key={y.year} className="px-3 py-1.5 rounded-full surface-2 text-sm">{y.year}</span>)}</div>
        </div>
      )}
    </div>
  );
}

function Btn({ children, icon: Icon, tone, ...p }) {
  const t = tone === "danger" ? "border-rose-500/30 text-rose-400 hover:bg-rose-500/10" : "border-white/15 text-white hover:bg-white/5";
  return <button {...p} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${t} disabled:opacity-40`}>{Icon && <Icon size={13} />}{children}</button>;
}

function IconBtn({ icon: Icon, title, tone, testid, ...p }) {
  const t = tone === "danger" ? "text-rose-400 hover:bg-rose-500/10" : tone === "gold" ? "text-gold hover:bg-gold/10" : "text-muted-foreground hover:bg-white/5 hover:text-white";
  return <button {...p} data-testid={testid} title={title} className={`w-8 h-8 rounded-lg flex items-center justify-center ${t}`}><Icon size={15} /></button>;
}
